<template>
  <div class="container">
    <div class="wrapper" :class="{ 'drawer-open': configDrawerSettings.open }">
      <div class="prev">
        <div class="list" :key="listKey">
          <AddItemCard
            v-if="items.filter((i) => i.type !== ItemType.Footer).length === 0"
            @open-drawer="handleOpenDrawer('add', undefined)"
          />

          <TemplateItemsSection
            :items="items"
            :templateType="template.template_type"
            @open-drawer="({ action, item }) => handleOpenDrawer(action, item)"
            @htmlStringEdited="handleHtmlStringEdited"
            @update:list="listKey += 1"
          />
        </div>
      </div>
    </div>

    <Transition name="slide-fade">
      <ConfigDrawerSection
        v-if="configDrawerSettings.open"
        :action="configDrawerSettings.action"
        :item="configDrawerSettings.item"
        :saving="saving"
        :template="template"
        @close="handleCloseDrawer"
        @add="handleAdd"
        @edited="handleEdit"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { provide, reactive, ref } from "vue";
import { useRoute } from "vue-router";
import { debounce } from "lodash";

import { ItemType, TemplateItem, TemplateItemOption } from "@/@types/flows/emailTemplates/items";
import { TemplateItemsManager } from "@/app/flows/emailTemplates/utils/TemplateItemsManager";
import { emailTemplateService } from "@/app/flows/emailTemplates/services/EmailTemplate.service";
import { TemplateHtmlBuilder } from "@/app/flows/emailTemplates/utils/TemplateHtmlBuilder";
import { Template } from "@/@types/flows/emailTemplates";

import ConfigDrawerSection from "@/app/flows/emailTemplates/components/sections/ConfigDrawerSection.vue";
import TemplateItemsSection from "@/app/flows/emailTemplates/components/sections/TemplateItemsSection.vue";
import AddItemCard from "@/app/flows/emailTemplates/components/cards/AddItemCard.vue";

const props = defineProps<{ template: Template; items: TemplateItem[] }>();

const configDrawerSettings: {
  open: boolean;
  action: "add" | "edit";
  item: TemplateItem | undefined;
} = reactive({ open: false, action: "add", item: undefined });

const selectedItem = ref<TemplateItem>();

const emit = defineEmits<{
  updateTemplate: any;
}>();

provide("selectedItem", selectedItem);

const handleOpenDrawer = (action: "add" | "edit", item: TemplateItem | undefined) => {
  configDrawerSettings.open = true;
  configDrawerSettings.action = action;
  configDrawerSettings.item = item;
};

const handleCloseDrawer = ({ updateList = false } = {}) => {
  if (updateList) listKey.value += 1;
  configDrawerSettings.open = false;
};

const route = useRoute();
const saving = ref(false);
const listKey = ref(1);

const handleHtmlStringEdited = debounce(() => {
  const items = TemplateItemsManager.getCurrentItems();
  const templateHtmlString = TemplateHtmlBuilder.generateHtmlTemplate(props.template, items);
  updateTemplate({ template: templateHtmlString });
}, 500);

const updateTemplate = async ({ template }: { template?: string } = {}) => {
  const templateId = Number(route.params.id);

  saving.value = true;

  try {
    const items = TemplateItemsManager.getCurrentItems();
    if (items?.[0]?.type == ItemType.Html) {
      // items[0].content = items[0].content.replace(/<a\s+([^>]*\s)?href="([^"]*)"(?![^>]*target)/g, '<a $1href="$2" target="_blank"');
      items[0].content = TemplateHtmlBuilder.generateHtmlLinks(items[0].content);
    }
    const templateHtmlString = TemplateHtmlBuilder.generateHtmlTemplate(props.template, items);
    await emailTemplateService.update(templateId, { items, template });
    emit("updateTemplate", { ...props.template, items: items, template: templateHtmlString });
  } catch (error) {
    console.error(error);
  }

  saving.value = false;
};

const handleAdd = async (option: TemplateItemOption) => {
  TemplateItemsManager.addItem(selectedItem.value?.id, option.type);
  await updateTemplate();
  handleCloseDrawer({ updateList: true });
};

const handleEdit = async () => {
  await updateTemplate();
  handleCloseDrawer({ updateList: true });
};
</script>

<style scoped lang="sass">
.container
  display: flex
  justify-content: space-between
  gap: 12px
  height: 100%

.list
  overflow: auto
  overflow-x: hidden
  max-height: calc(100vh - 167px)

.wrapper
  flex: 1 0 100%
  display: flex
  justify-content: center
  transition: flex-basis 0.3s ease

  &.drawer-open
    flex: 1 0 50%

.prev
  border-radius: 4px
  margin-top: 12px
  flex: 1 1 auto
  max-width: 50vw
  // background-color: white
  position: relative
  height: fit-content
  overflow: hidden
</style>
